import { useCallback, useRef, useState } from 'react';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Props {
  value: string | null;
  onChange: (url: string | null) => void;
}

async function resizeClientSide(file: File): Promise<Blob> {
  const img = new Image();
  img.src = URL.createObjectURL(file);
  await new Promise<void>((res, rej) => {
    img.onload = () => res();
    img.onerror = () => rej(new Error('Could not load image'));
  });
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 200;
  const ctx = canvas.getContext('2d')!;
  const size = Math.min(img.width, img.height);
  const sx = (img.width - size) / 2;
  const sy = (img.height - size) / 2;
  ctx.drawImage(img, sx, sy, size, size, 0, 0, 200, 200);
  URL.revokeObjectURL(img.src);
  return await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), 'image/jpeg', 0.85));
}

export function PhotoPicker({ value, onChange }: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const uploadFile = useCallback(
    async (file: File) => {
      setBusy(true);
      setError(null);
      try {
        const blob = await resizeClientSide(file);
        const form = new FormData();
        form.append('file', blob, 'photo.jpg');
        const res = await api.post<{ url: string }>('/uploads/photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        onChange(res.data.url);
        toast('Photo uploaded');
      } catch (err) {
        console.error(err);
        setError('Upload failed');
        toast('Upload failed', undefined, 'danger');
      } finally {
        setBusy(false);
      }
    },
    [onChange],
  );

  const uploadUrl = useCallback(async () => {
    if (!urlInput) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(urlInput, { mode: 'cors' });
      const blob = await resp.blob();
      const file = new File([blob], 'photo.jpg', { type: blob.type });
      await uploadFile(file);
      setUrlInput('');
    } catch {
      setError('Could not fetch image from URL');
    } finally {
      setBusy(false);
    }
  }, [urlInput, uploadFile]);

  return (
    <div className="pp-wrap">
      <div
        className={`pp-preview ${value ? 'pp-has-photo' : ''} ${dragOver ? 'pp-drag' : ''}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) uploadFile(f);
        }}
      >
        {value ? <img src={value} alt="" /> : <div className="pp-preview-hint">Click, drag,<br />or paste URL</div>}
      </div>
      <div className="pp-controls">
        <div className="pp-btn-row">
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => fileRef.current?.click()} disabled={busy}>Pick file</button>
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => onChange(null)} disabled={busy || !value}>Remove</button>
        </div>
        <input
          className="pp-url"
          placeholder="…or paste image URL"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onBlur={() => urlInput && uploadUrl()}
        />
        {error ? <div className="pp-err show">{error}</div> : null}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); }}
        />
      </div>
    </div>
  );
}
